import { Section, useConfig } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack } from 'braid-design-system';
import { getCountryNameByCode } from '../../../../shared/utils/getCountryNameByCode';
import { useScrollToTop } from '../../../../shared/utils/useScrollToTop';
import type { SalaryRangeByRole } from '../../../pages/salaries/salariesTabModel';
import translations from './.vocab/index';
import { SalariesList } from './SalariesList';
import { SalariesSearchBar } from './SalariesSearchBar';

const MINIMUM_ROLES_TO_SHOW = 7;

interface SalariesListingSectionProps {
  companyName: string;
  salaryRangeByRoles: SalaryRangeByRole[];
  isSalariesByRole?: boolean;
}

export const SalariesListingSection = ({
  salaryRangeByRoles,
  companyName,
  isSalariesByRole = false,
}: SalariesListingSectionProps) => {
  const { t } = useTranslations(translations);
  const { country, language } = useConfig();
  const { scrollToTopRef, scrollToTop } = useScrollToTop('SECTION');

  if (isSalariesByRole) {
    return (
      <Box ref={scrollToTopRef}>
        <Section
          mode={'view'}
          heading={t('More companyName salaries', { companyName })}
          padding={'none'}
        >
          <SalariesList
            salaryRangeByRoles={salaryRangeByRoles}
            isSalariesByRole={isSalariesByRole}
            scrollToTop={scrollToTop}
          />
        </Section>
      </Box>
    );
  }

  const hasEnoughRolesForSearch =
    salaryRangeByRoles.length >= MINIMUM_ROLES_TO_SHOW;

  return (
    <Section
      mode={'view'}
      heading={t('Company salaries', { companyName })}
      headingDescription={t('Explore how much...', {
        companyName,
        country: getCountryNameByCode(country, language),
      })}
      padding={'none'}
    >
      {hasEnoughRolesForSearch ? (
        <Stack space="large">
          <SalariesSearchBar salaryRangeByRoles={salaryRangeByRoles} />
          <Box ref={scrollToTopRef}>
            <Heading level="3" component="h2">
              {t('Salaries at companyName', { companyName })}
            </Heading>
          </Box>
          <SalariesList
            salaryRangeByRoles={salaryRangeByRoles}
            scrollToTop={scrollToTop}
          />
        </Stack>
      ) : (
        <Box ref={scrollToTopRef}>
          <SalariesList
            salaryRangeByRoles={salaryRangeByRoles}
            scrollToTop={scrollToTop}
          />
        </Box>
      )}
    </Section>
  );
};
